import { kebabCase, truncate } from 'lodash-es';

export const removeCountryName = (address: string | undefined): string => (address ? address.split(/, \S+$/g)[0] : '');

export const formatLocationDisplay = (formattedAddress?: string, postalCode?: string | null, city?: string | null): string => {
  if (city && postalCode) return `${postalCode} ${city}`;
  return removeCountryName(formattedAddress) || city || postalCode || '';
};

interface JobPositionLocation {
  city?: string | null;
  state?: string | null;
  administrativeAreaLevel1?: string | null;
  administrativeAreaLevel2?: string | null;
  administrativeAreaLevel3?: string | null;
  administrativeAreaLevel4?: string | null;
  administrativeAreaLevel5?: string | null;
  country?: string | null;
}

type GeoLocation = JobPositionLocation | null;

export type JobPosition = {
  id: string;
  title?: string;
  geoLocation?: GeoLocation;
  slug?: string | null;
};

const JOB_ROUTE = '/job';

const getValidLocationValue = (value?: string | null) => (value?.trim() ? value.trim() : null);

export const getCityFallback = (geoLocation?: GeoLocation) =>
  [
    geoLocation?.city,
    geoLocation?.state,
    geoLocation?.administrativeAreaLevel1,
    geoLocation?.administrativeAreaLevel2,
    geoLocation?.administrativeAreaLevel3,
    geoLocation?.administrativeAreaLevel4,
    geoLocation?.administrativeAreaLevel5,
    geoLocation?.country,
  ]
    .map(getValidLocationValue)
    .find((value): value is string => Boolean(value));

export const getJobPositionPathname = (
  jobPosition: JobPosition,
  options: {
    preview?: boolean;
    basePath?: string;
    source?: string;
    applicationSource?: string;
    registerOrigin?: string;
    slimmed?: boolean;
    utmParams?: Record<string, string | undefined>;
  } = {},
) => {
  const { preview, source, basePath = JOB_ROUTE, applicationSource, registerOrigin, slimmed, utmParams } = options;
  if (preview) return `/job/${jobPosition.id}/preview`;
  const pathname = [basePath, getCityFallback(jobPosition.geoLocation), jobPosition.slug || jobPosition.title]
    .map(value => encodeURIComponent(kebabCase(value || 'unknown')))
    .join('/');
  const queryParams = new URLSearchParams();
  if (source) queryParams.append('source', source);
  if (applicationSource) queryParams.append('applicationSource', applicationSource);
  if (registerOrigin) queryParams.append('registerOrigin', registerOrigin);
  if (typeof slimmed === 'boolean') queryParams.append('slimmed', slimmed ? 'true' : 'false');
  if (utmParams)
    for (const [key, value] of Object.entries(utmParams)) {
      if (value) queryParams.append(key, value);
    }

  const stringQueryParams = queryParams.toString() ? `#${queryParams.toString()}` : '';
  return `/${pathname}/${jobPosition.id}${stringQueryParams}`;
};

const MAX_JOB_POSITION_TITLE_LENGTH = 24;
const DOTS_LENGTH = 3;
const NUMBER_OF_WORDS = 3;

export const truncateJobPositionTitle = (jobPositionTitle: string) => {
  if (jobPositionTitle.length < MAX_JOB_POSITION_TITLE_LENGTH) return jobPositionTitle;

  const words = jobPositionTitle.split(' ');
  const wordsAtStart = words.slice(0, NUMBER_OF_WORDS).join(' ');

  return truncate(jobPositionTitle, { length: Math.max(wordsAtStart.length, MAX_JOB_POSITION_TITLE_LENGTH) + DOTS_LENGTH });
};
